import { type JSX, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { ActionsApi } from 'Api/Actions/ActionsApi';
import { DashboardApi } from 'Api/Dashboards/DashboardApi';
import { ExceptionsApi } from 'Api/Exceptions/ExceptionsApi';
import { IssuesApi } from 'Api/Issues/IssuesApi';
import { RiskRegisterApi } from 'Api/RiskRegister/RiskRegisterApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { RBACContext, Role, userHasAuthorizedRole } from 'Components/Context/RBACContext';
import { PageLayoutDashboardTabs } from 'Components/PageLayout/PageLayoutDashboard';
import { CONTROL_AUTOMATION_ROLES, RISK_ROLES, TPRM_ROLES, UCRM_ROLES } from 'Helpers/UserUtils';
import { Action } from 'Models/Actions';
import { AdvancedFeature } from 'Models/ClientDetailsModel';
import { DashboardType } from 'Models/Dashboards';
import { ExceptionResponse } from 'Models/Exceptions';
import { IssueResponse } from 'Models/Issues';
import { ProductModule } from 'Models/ProductModule';
import { ResponseModel } from 'Models/ResponseModel';
import { ALL_RISKS, RiskCategoryResponse, RiskResponse } from 'Models/RiskRegister';

import { DashboardsHistoricalExceptions } from './Components/DashboardsHistoricalExceptions/DashboardsHistoricalExceptions';
import { DashboardsHistoricalIssues } from './Components/DashboardsHistoricalIssues/DashboardsHistoricalIssues';
import { DashboardsTrackingExceptions } from './Components/DashboardsTrackingExceptions/DashboardsTrackingExceptions';
import { DashboardsTrackingIssues } from './Components/DashboardsTrackingIssues/DashboardsTrackingIssues';
import { useDueDiligenceDashboard } from './DueDiligenceDashboard/DueDiligenceDashboard';
import { AssessmentsSchedule } from './OperationalControlsDashboard/AssessmentsSchedule/AssessmentsSchedule';
import { ComplianceRequirements } from './OperationalControlsDashboard/ComplianceRequirements/ComplianceRequirements';
import { ControlStrength } from './OperationalControlsDashboard/ControlStrength/ControlStrength';
import { ControlsOverTime } from './OperationalControlsDashboard/ControlsOverTime/ControlsOverTime';
import { useControlStrengthQuery, useCurrentComplianceSummaryQuery } from './OperationalControlsDashboard/OperationalControlsDashboard.hooks';
import { RequirementsOverview } from './OperationalControlsDashboard/RequirementsOverview/RequirementsOverview';
import { ActionHistory } from './RiskRegisterDashboard/ActionHistory';
import { ActionTracking } from './RiskRegisterDashboard/ActionTracking';
import { RiskCallouts } from './RiskRegisterDashboard/RiskCallouts';
import { RiskMatrix } from './RiskRegisterDashboard/RiskMatrix';

const RISK_MANAGEMENT_DASHBOARD_ROLES = [Role.REPORTING_USER, ...RISK_ROLES];
const CONTROL_AUTOMATION_DASHBOARD_ROLES = [Role.REPORTING_USER, ...CONTROL_AUTOMATION_ROLES];
const TPRM_DASHBOARD_ROLES = [Role.REPORTING_USER, ...TPRM_ROLES];
const UCRM_DASHBOARD_ROLES = [Role.REPORTING_USER, ...UCRM_ROLES];

export interface DashboardsProps {
    actionsApi: ActionsApi;
    dashboardApi: DashboardApi;
    exceptionsApi: ExceptionsApi;
    issuesApi: IssuesApi;
    ucrmApi: ClientUcrmApi;
    riskRegisterApi: RiskRegisterApi;
    tprmApi: ClientTPRMApi;
}

export const Dashboards = (props: DashboardsProps): JSX.Element => {
    const userContext = useContext(RBACContext)!;
    const cachedData = useCachedData();

    const navigate = useNavigate();
    const location = useLocation();

    const userCanViewControlAutomationDashboard = userHasAuthorizedRole(userContext.roles, CONTROL_AUTOMATION_DASHBOARD_ROLES);
    const userCanViewRiskManagementDashboard = userHasAuthorizedRole(userContext.roles, RISK_MANAGEMENT_DASHBOARD_ROLES);
    const userCanViewTprmDashboard = userHasAuthorizedRole(userContext.roles, TPRM_DASHBOARD_ROLES);
    const clientHasUcrm = cachedData.clientHasAdvancedFeature([AdvancedFeature.UCRM]);
    const userCanViewUcrmDashboard = clientHasUcrm && userHasAuthorizedRole(userContext.roles, UCRM_DASHBOARD_ROLES);
    const userCanFetchIssuesAndExceptions = userCanViewControlAutomationDashboard || userCanViewTprmDashboard || userCanViewUcrmDashboard;

    const [controlStrength, controlStrengthError] = useControlStrengthQuery(props.dashboardApi, userCanViewControlAutomationDashboard);
    const [complianceSummaries, complianceSummaryError] = useCurrentComplianceSummaryQuery(props.dashboardApi, userCanViewControlAutomationDashboard);

    const [overallControlEffectiveness, setOverallControlEffectiveness] = useState<number>();
    const [riskCategories, setRiskCategories] = useState<RiskCategoryResponse[]>();
    const [risks, setRisks] = useState<RiskResponse[]>();
    const [risksError, setRisksError] = useState<Error>();
    const [allExceptions, setAllExceptions] = useState<ExceptionResponse[]>();
    const [allIssues, setAllIssues] = useState<IssueResponse[]>();
    const [allActions, setAllActions] = useState<Action[]>();
    const [exceptionsError, setExceptionsError] = useState<string>();
    const [issuesError, setIssuesError] = useState<string>();
    const [actionsError, setActionsError] = useState<string>();

    useEffect(() => {
        const getOverallControlEffectiveness = async (): Promise<void> => {
            try {
                const effectivenessResponse = await props.riskRegisterApi.getOverallControlEffectiveness();
                setOverallControlEffectiveness(effectivenessResponse.data);
            } catch (error) {
                setRisksError(error);
            }
        };

        const getRiskCategories = async (): Promise<void> => {
            try {
                const response = await props.riskRegisterApi.getRiskCategories();
                setRiskCategories([{ title: ALL_RISKS, id: ALL_RISKS, in_use: true }, ...response.data]);
            } catch (error) {
                setRisksError(error);
            }
        };

        const getRisks = async (): Promise<void> => {
            try {
                const riskResponse: ResponseModel<RiskResponse[]> = await props.riskRegisterApi.getAllRisks();
                setRisks(riskResponse.data);
            } catch (error) {
                setRisksError(error);
            }
        };

        if (userCanViewRiskManagementDashboard) {
            getOverallControlEffectiveness();
            getRiskCategories();
            getRisks();
        }
    }, [props.riskRegisterApi, userCanViewRiskManagementDashboard]);

    useEffect(() => {
        const getActions = async (): Promise<void> => {
            try {
                const response = await props.actionsApi.getAllActions();
                setAllActions(response.data);
            } catch (error) {
                setActionsError(error.message);
            }
        };

        if (userCanViewRiskManagementDashboard) {
            getActions();
        }
    }, [props.actionsApi, userCanViewRiskManagementDashboard]);

    useEffect(() => {
        const getIssues = async (): Promise<void> => {
            try {
                const response = await props.issuesApi.getAllIssues();
                setAllIssues(response.data);
            } catch (error) {
                setIssuesError(error.message);
            }
        };

        if (userCanFetchIssuesAndExceptions) {
            getIssues();
        }
    }, [props.issuesApi, userCanFetchIssuesAndExceptions]);

    useEffect(() => {
        const getExceptions = async (): Promise<void> => {
            try {
                const response = await props.exceptionsApi.getAllExceptions();
                setAllExceptions(response.data);
            } catch (error) {
                setExceptionsError(error.message);
            }
        };

        if (userCanFetchIssuesAndExceptions) {
            getExceptions();
        }
    }, [props.exceptionsApi, userCanFetchIssuesAndExceptions]);

    const activeHash = (() => {
        if (location.hash && location.hash.length > 0) {
            const strippedHash = location.hash.substring(1);
            if (Object.values(DashboardType).includes(strippedHash as DashboardType)) {
                return strippedHash;
            }
        }
        return DashboardType.RISK_MANAGEMENT;
    })();

    const onTabSelect = (key?: string) => {
        if (key) {
            navigate(`${location.pathname}#${key}`, { replace: true });
        }
    };

    const issuesAndExceptions = { allExceptions, allIssues, exceptionsError, issuesError };
    const tprmDashboard = useDueDiligenceDashboard({ ...issuesAndExceptions, dashboardApi: props.dashboardApi, module: ProductModule.TPRM, moduleApi: props.tprmApi, userIsAuthorized: userCanViewTprmDashboard });
    const ucrmDashboard = useDueDiligenceDashboard({ ...issuesAndExceptions, dashboardApi: props.dashboardApi, module: ProductModule.UCRM, moduleApi: props.ucrmApi, userIsAuthorized: userCanViewUcrmDashboard });

    return (
        <>
            {tprmDashboard.modal}
            {ucrmDashboard.modal}
            <PageLayoutDashboardTabs
                headerTitle="Dashboards"
                defaultTab={activeHash}
                onTabChange={onTabSelect}
                tabs={[
                    {
                        title: DashboardType.RISK_MANAGEMENT,
                        roles: RISK_MANAGEMENT_DASHBOARD_ROLES,
                        dashlets: [
                            {
                                content: <RiskCallouts error={risksError} overallControlEffectiveness={overallControlEffectiveness} riskCategories={riskCategories} risks={risks} />,
                            },
                            {
                                content: <RiskMatrix error={risksError} riskCategories={riskCategories} risks={risks} />,
                            },
                            {
                                dashlets: [
                                    {
                                        content: <ActionHistory allActions={allActions} actionsError={actionsError} />,
                                    },
                                    {
                                        content: <ActionTracking allActions={allActions} actionsError={actionsError} />,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        title: DashboardType.CONTROL_AUTOMATION,
                        roles: CONTROL_AUTOMATION_DASHBOARD_ROLES,
                        dashlets: [
                            {
                                dashlets: [
                                    {
                                        title: 'Overall Control Effectiveness',
                                        content: <ControlStrength controlStrength={controlStrength} controlStrengthError={controlStrengthError} />,
                                    },
                                    {
                                        title: 'Compliance Requirements',
                                        content: <RequirementsOverview complianceSummaries={complianceSummaries} complianceSummaryError={complianceSummaryError} api={props.dashboardApi} />,
                                    },
                                ],
                            },
                            {
                                content: <ControlsOverTime dashboardApi={props.dashboardApi} controlStrength={controlStrength} controlStrengthError={controlStrengthError} />,
                            },
                            {
                                title: 'Assessment Schedule',
                                content: <AssessmentsSchedule api={props.dashboardApi} />,
                            },
                            {
                                content: <ComplianceRequirements complianceSummaries={complianceSummaries} complianceSummaryError={complianceSummaryError} api={props.dashboardApi} />,
                            },
                            {
                                dashlets: [
                                    {
                                        content: <DashboardsHistoricalIssues allIssues={allIssues} issuesError={issuesError} dashboardType={DashboardType.CONTROL_AUTOMATION} />,
                                    },
                                    {
                                        content: <DashboardsTrackingIssues allIssues={allIssues} issuesError={issuesError} dashboardType={DashboardType.CONTROL_AUTOMATION} />,
                                    },
                                ],
                            },
                            {
                                dashlets: [
                                    {
                                        content: <DashboardsHistoricalExceptions allExceptions={allExceptions} exceptionsError={exceptionsError} dashboardType={DashboardType.CONTROL_AUTOMATION} />,
                                    },
                                    {
                                        content: <DashboardsTrackingExceptions allExceptions={allExceptions} exceptionsError={exceptionsError} dashboardType={DashboardType.CONTROL_AUTOMATION} />,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        title: DashboardType.THIRD_PARTY_RISK_MANAGEMENT,
                        roles: TPRM_DASHBOARD_ROLES,
                        dashlets: tprmDashboard.dashlets,
                    },
                    // `PageLayoutDashboardTabs` filters tabs by role only, so the tab is omitted entirely when the client does not have the UCRM license.
                    ...(clientHasUcrm
                        ? [
                              {
                                  title: DashboardType.UCRM,
                                  roles: UCRM_DASHBOARD_ROLES,
                                  dashlets: ucrmDashboard.dashlets,
                              },
                          ]
                        : []),
                ]}
            />
        </>
    );
};
